/**
 * Ask's button in the bottom right corner of every page but the Ask page (the Ask page sketch): the
 * orb and "Analüütik". It opens a panel above it that asks from this page: its filters go along (a chip
 * says so, and can be taken off), its example questions are offered, the question's steps tick
 * off as Ask reads, and the answer shows short with "Open in full view", which opens its thread on
 * the Ask page. A follow-up stays in the same thread. On a phone the panel is the whole screen.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { cx } from '@/kit/cx'
import { ASK_PATH } from './askApi'
import { activeStep, countsOf } from './askRun'
import { AnswerCard } from './AnswerCard'
import { useAsk } from './AskProvider'
import { Composer } from './AskPage'
import { makeAskFormats } from './format'
import { Mark, Orb } from './Mark'
import './ask.css'
import './i18n'

export interface AskButtonPage {
    id: string
    title: string
}

export function AskButton({ page }: { page: AskButtonPage | null }) {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const ask = useAsk()
    const [open, setOpen] = useState(false)
    // The page the person took the page's filters off for: back on when they go to another page.
    const [droppedOn, setDroppedOn] = useState<string | null>(null)
    const panelRef = useRef<HTMLElement>(null)
    const fmt = makeAskFormats(i18n.language)

    // The page's filters, as its URL holds them (chain, county…): what a question from here goes by.
    const state = useMemo(() => {
        const out: Record<string, string> = {}
        new URLSearchParams(location.search).forEach((value, key) => {
            if (/^[a-z_]{1,40}$/.test(key) && value && value.length <= 200 && key !== 'sort') out[key] = value
        })
        return out
    }, [location.search])

    useEffect(() => {
        if (!open) return undefined
        const onKey = (e: globalThis.KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const byPage = page !== null && droppedOn !== page.id
    if (!ask || location.pathname === ASK_PATH || location.pathname.startsWith(`${ASK_PATH}/`)) return null
    const run = ask.run
    const running = run?.status === 'running'
    const examples = (ask.status?.examples ?? []).filter((e) => e.page === (page?.id ?? null) || e.page === null).sort((a, b) => Number(b.page !== null) - Number(a.page !== null)).slice(0, 3)
    const filters = Object.entries(state).map(([k, v]) => `${k} ${v}`).join(' · ')
    const contextLabel = page && byPage ? t('ask.context.page', { page: [page.title, filters].filter(Boolean).join(' · ') }) : t('ask.context.all')
    const counts = run ? countsOf(run.steps) : null
    const active = run ? activeStep(run.steps) : null

    const onAsk = (question: string) => {
        ask.ask({
            question,
            page: byPage ? (page?.id ?? null) : null,
            state: byPage ? state : {},
            // A follow-up stays in the thread of the answer above it.
            threadId: run && run.status !== 'running' ? run.threadId : null,
        })
    }

    return (
        <>
            {open && (
                <section
                    ref={panelRef}
                    aria-label={t('ask.title')}
                    className="ask-pop fixed right-[24px] bottom-[84px] z-40 box-border flex max-h-[calc(100vh-120px)] w-[400px] max-w-[calc(100vw-32px)] flex-col gap-[12px] overflow-y-auto rounded-[14px] bg-[var(--surface-raised)] px-[16px] pt-[14px] pb-[16px] shadow-[0_0_0_1px_#0000000d,0_12px_40px_rgba(16,16,19,0.16)] max-sm:inset-0 max-sm:max-h-none max-sm:w-auto max-sm:max-w-none max-sm:rounded-none"
                    data-testid="ask-panel"
                >
                    <div className="flex items-center gap-[8px]">
                        <Mark width={9} color="var(--text-default)" />
                        <span className="flex-1 text-[14px] font-[650] text-[var(--text-default)]">{t('ask.title')}</span>
                        {/* While it reads; once it has answered, the answer's own button opens it. */}
                        {run?.threadId && running && (
                            <Link to={`${ASK_PATH}/${run.threadId}`} onClick={() => setOpen(false)} className="inline-flex items-center gap-[4px] rounded-[6px] px-[8px] py-[6px] text-[12.5px] font-[600] text-[var(--text-link)] no-underline hover:bg-[var(--interaction-hover)]">
                                {t('ask.openFull')}
                                <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M8 16L16 8M9 8h7v7" />
                                </svg>
                            </Link>
                        )}
                        <button type="button" aria-label={t('ask.close')} onClick={() => setOpen(false)} className="inline-flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[6px] border-0 bg-transparent text-[var(--text-weak)] hover:bg-[var(--interaction-hover)]">
                            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>
                    </div>

                    {page && byPage && (
                        <div className="flex items-center gap-[6px] rounded-[8px] bg-[var(--accent-weak)] py-[6px] pr-[6px] pl-[10px] text-[12px] leading-[16px] text-[var(--text-weak)]">
                            <span className="min-w-0 flex-1">{contextLabel}</span>
                            <button type="button" aria-label={t('ask.context.drop')} title={t('ask.context.drop')} onClick={() => setDroppedOn(page.id)} className="inline-flex h-[24px] w-[24px] cursor-pointer items-center justify-center rounded-[4px] border-0 bg-transparent text-[var(--text-weak)] hover:bg-[var(--interaction-hover)]">
                                <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                    <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {!run && examples.length > 0 && (
                        <div className="flex flex-col gap-[8px]">
                            <span className="text-[12px] text-[var(--text-weaker)]">{t('ask.examples')}</span>
                            {examples.map((e) => (
                                <button key={e.text} type="button" onClick={() => onAsk(e.text)} className="cursor-pointer rounded-[8px] border border-solid border-[var(--border-strong)] bg-[var(--surface-raised)] px-[10px] py-[8px] text-left text-[13px] leading-[18px] text-[var(--text-default)] hover:bg-[var(--interaction-hover)]">
                                    {e.text}
                                </button>
                            ))}
                        </div>
                    )}

                    {run && <div className="max-w-[320px] self-end rounded-[10px] bg-[var(--surface-header)] px-[12px] py-[8px] text-[13.5px] leading-[19px] font-[550] text-[var(--text-default)]">{run.question}</div>}

                    {run && running && (
                        <div className="flex flex-col gap-[10px]">
                            <div className="flex items-center gap-[12px]">
                                <Orb size={36} halo={1.6} />
                                <div role="status" aria-live="polite">
                                    <div className="text-[13px] leading-[18px] font-[650] text-[var(--text-default)]">{active?.purpose ? `${t(`ask.stage.${run.stage}`)}: ${active.purpose}` : t(`ask.stage.${run.stage}`)}</div>
                                    {counts && <div className="text-[11.5px] leading-[16px] text-[var(--text-weaker)]">{[t('ask.count.queries', { count: counts.queries }), t('ask.count.rows', { count: counts.rows })].join(' · ')}</div>}
                                </div>
                            </div>
                            <ul className="m-0 flex list-none flex-col gap-[4px] p-0">
                                {run.steps.map((s) => (
                                    <li key={s.id} className={cx('ask-in flex min-h-[28px] items-center gap-[8px] rounded-[6px] border border-solid border-[var(--border-default)] px-[8px] py-[4px] text-[12.5px] leading-[18px]', s.state === 'running' && 'bg-[var(--accent-weak)]')}>
                                        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={s.state === 'done' ? 'var(--text-success)' : 'var(--accent-default)'} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                            {s.state === 'done' ? <path d="M5 12l5 5 9-10" /> : <path d="M5 20v-7M10 20V6M15 20v-10M20 20v-4" />}
                                        </svg>
                                        <span className="min-w-0 flex-1 truncate text-[var(--text-default)]">{s.tool === 'load_skill' ? s.title : s.purpose}</span>
                                        <span className={cx('font-[650] whitespace-nowrap tabular-nums', s.state === 'running' ? 'ask-reading text-[var(--text-weaker)]' : 'text-[var(--text-default)]')}>
                                            {s.state === 'running' ? t('ask.reading') : s.state === 'failed' ? t('ask.stepFailed') : s.tool === 'load_skill' ? t('ask.skillRead') : (fmt.figures(s) ?? t('ask.count.rows', { count: s.rows ?? 0 }))}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {run && !running && run.answer && <AnswerCard answer={run.answer} numberCheck={run.numberCheck} steps={run.steps} animate compact onAsk={onAsk} />}
                    {run && !running && !run.answer && (
                        <p className="m-0 text-[13px] leading-[19px] text-[var(--text-weak)]" role="alert">
                            {t(`ask.error.${run.error && ['over_budget', 'not_configured', 'forbidden_by_role', 'no_answer'].includes(run.error) ? run.error : run.status === 'stopped' ? 'stopped' : 'failed'}`)}
                        </p>
                    )}
                    {run && !running && run.answer && run.threadId && (
                        <Link to={`${ASK_PATH}/${run.threadId}`} onClick={() => setOpen(false)} className="press inline-flex h-[36px] items-center justify-center gap-[6px] self-start rounded-[8px] px-[14px] text-[14px] font-[600] text-[var(--text-on-accent)] no-underline shadow-[var(--shadow-button-primary)] [background:var(--button-primary-fill)]">
                            {t('ask.openFull')}
                        </Link>
                    )}

                    {/* The page's filters are the chip above; without them, "all data" goes by the box. */}
                    <Composer onAsk={onAsk} placeholder={run ? t('ask.placeholderNext') : t('ask.placeholder')} context={page && byPage ? undefined : contextLabel} autoFocus busy={running} />
                </section>
            )}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid="ask-button"
                className={cx(
                    'fixed right-[24px] bottom-[24px] z-40 inline-flex h-[48px] cursor-pointer items-center gap-[10px] rounded-full border-0 bg-[var(--surface-raised)] pr-[18px] pl-[8px] text-[14px] font-[650] text-[var(--text-default)] shadow-[0_0_0_1px_#0000000d,0_6px_20px_rgba(16,16,19,0.14)] transition-transform duration-150 hover:-translate-y-[1px] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none print:hidden motion-reduce:transition-none max-sm:right-[16px] max-sm:bottom-[16px]',
                    // On a phone the open panel is the whole screen, with its own close button.
                    open && 'max-sm:hidden',
                )}
            >
                <Orb size={32} halo={1.25} calm={!running} />
                {t('ask.button')}
            </button>
        </>
    )
}
